import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Button,
  Fade,
  Paper,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { CheckCircle, ArrowBack, Schedule, Home } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

const ServiceLayout = ({ 
  title, 
  image, 
  description, 
  listItems, 
  additionalContent,
  benefits,
  approach 
}) => {
  const navigate = useNavigate();

  return (
    <Fade in timeout={800}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ 
          mb: 3, 
          mt: 2,
          '& .MuiTypography-root': {
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }
        }}>
          <MuiLink 
            component={Link} 
            to="/" 
            color="inherit"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              '&:hover': { color: 'primary.main' }
            }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </MuiLink>
          <MuiLink 
            component={Link} 
            to="/services" 
            color="inherit"
            sx={{ '&:hover': { color: 'primary.main' } }}
          >
            Services
          </MuiLink>
          <Typography color="text.primary">{title}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            {/* Hero Section */}
            <Fade in timeout={1000}>
              <Card sx={{ 
                borderRadius: 4, 
                overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                mb: { xs: 3, sm: 4 }
              }}>
                <CardMedia
                  component="img"
                  height={{ xs: '300', sm: '400', md: '500' }}
                  image={image}
                  alt={title}
                  sx={{ 
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              </Card>
            </Fade>

            {/* Title and Description */}
            <Fade in timeout={1200}>
              <Box>
                <Typography 
                  variant="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    background: 'linear-gradient(45deg, #009688, #4CAF50)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: { xs: 2, sm: 3 }
                  }}
                >
                  {title}
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.2rem' }, 
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    mb: { xs: 3, sm: 4 }
                  }}
                >
                  {description}
                </Typography>
              </Box>
            </Fade>

            {/* Benefits and Approach Section */}
            <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mb: { xs: 3, sm: 4 } }}>
              {/* Benefits Card */}
              <Grid item xs={12} md={6}>
                <Fade in timeout={1400}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: { xs: 3, sm: 4 }, 
                      height: '100%',
                      borderRadius: 4,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(135deg, rgba(0,150,136,0.05) 0%, rgba(76,175,80,0.05) 100%)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 24px rgba(0,150,136,0.15)'
                      }
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 3,
                        fontSize: { xs: '1.75rem', sm: '2rem' }
                      }}
                    >
                      Key Benefits
                    </Typography>
                    <List>
                      {benefits?.map((item, index) => (
                        <ListItem 
                          key={index}
                          sx={{
                            py: { xs: 1.5, sm: 1 },
                            px: 0
                          }}
                        >
                          <ListItemIcon>
                            <CheckCircle color="primary" sx={{ fontSize: { xs: 24, sm: 20 } }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: { xs: '1.1rem', sm: '1.1rem' }
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Fade in timeout={1600}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 4,
                      height: '100%',
                      borderRadius: 4,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(135deg, rgba(76,175,80,0.05) 0%, rgba(0,150,136,0.05) 100%)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 24px rgba(0,150,136,0.15)'
                      }
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 3
                      }}
                    >
                      Our Approach
                    </Typography>
                    <List>
                      {approach?.map((item, index) => (
                        <ListItem 
                          key={index}
                          sx={{
                            py: 1,
                            px: 0
                          }}
                        >
                          <ListItemIcon>
                            <CheckCircle color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: '1.1rem'
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Fade>
              </Grid>
            </Grid>

            {additionalContent}

            {/* Navigation Buttons */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
              justifyContent: 'space-between',
              mt: { xs: 4, sm: 6 },
              pt: { xs: 3, sm: 4 },
              borderTop: 1,
              borderColor: 'divider'
            }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/services')}
                fullWidth={false}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: 'inherit' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateX(-5px)'
                  }
                }}
              >
                Back to Services
              </Button>
              <Button
                variant="contained"
                endIcon={<Schedule />}
                onClick={() => navigate('/book-appointment')}
                fullWidth={false}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: 'inherit' },
                  background: 'linear-gradient(45deg, #009688, #4CAF50)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #00897B, #43A047)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,150,136,0.4)'
                  }
                }}
              >
                Book Appointment
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Fade>
  );
};

export default ServiceLayout; 